import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { useCountry } from "../lib/country";
import { useLanguage } from "../lib/language";
import { ShoppingBag, User, Menu, X, BadgeCheck, LogOut } from "lucide-react";
import { api } from "../lib/api";
import { getPublicName } from "../lib/publicName";

export default function Navbar() {
    const { user, logout } = useAuth();
    const { count } = useCart();
    // country context still exists but is hard-coded to SV — we keep the
    // import in case future callers need it, but no UI switcher is exposed.
    useCountry();
    const { t } = useLanguage();
    const nav = useNavigate();
    const loc = useLocation();
    const [open, setOpen] = useState(false);
    const [logo, setLogo] = useState("");

    useEffect(() => {
        api.get("/settings").then(({ data }) => setLogo(data.logo || ""));
    }, []);

    // Handle Emergent Google auth redirect (#session_id=...)
    const { googleLogin } = useAuth();
    useEffect(() => {
        if (window.location.hash.includes("session_id=")) {
            const sid = window.location.hash.split("session_id=")[1].split("&")[0];
            googleLogin(sid).then(() => {
                window.history.replaceState(null, "", window.location.pathname);
                nav("/");
            }).catch(console.error);
        }
        // eslint-disable-next-line
    }, []);

    const links = [
        { to: "/", label: t("Inicio", "Home") },
        { to: "/tienda", label: t("Tienda", "Shop") },
        { to: "/solicitar-producto", label: t("Solicitar", "Request") },
        { to: "/vendedores", label: t("Vendedores", "Sellers") },
        { to: "/tracking", label: t("Rastrear", "Track") },
        { to: "/reviews", label: t("Reseñas", "Reviews") },
        { to: "/como-usar", label: t("Cómo Usar", "How to use") },
        { to: "/soporte", label: t("Soporte", "Support") },
    ];

    return (
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-[var(--tb-bg)]/80 border-b border-[var(--tb-border)]">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 md:py-4 flex items-center justify-between gap-2">
                <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0 flex-shrink" data-testid="nav-logo">
                    {logo ? <img src={logo} alt="" className="h-9 w-9 sm:h-10 sm:w-10 md:h-12 md:w-12 object-cover rounded-full border border-[var(--tb-gold)] flex-shrink-0" /> :
                        <div className="h-9 w-9 sm:h-10 sm:w-10 md:h-12 md:w-12 rounded-full bg-[var(--tb-gold)] flex items-center justify-center text-white font-bold font-display text-base md:text-xl flex-shrink-0">T</div>}
                    <div className="min-w-0">
                        <div className="font-display text-base sm:text-lg md:text-2xl tracking-tight leading-none whitespace-nowrap truncate">Terra Boutique</div>
                        <div className="tb-overline text-[var(--tb-gold)] text-[9px] sm:text-[10px] mt-0.5 sm:mt-1">El Salvador</div>
                    </div>
                </Link>

                <nav className="hidden xl:flex items-center gap-5 2xl:gap-7">
                    <Link to="/" className={`tb-link text-sm tracking-wide whitespace-nowrap ${loc.pathname === "/" ? "text-[var(--tb-gold)]" : ""}`} data-testid="nav-link-home">
                        {t("Inicio", "Home")}
                    </Link>
                    {links.slice(1).map((l) => (
                        <Link key={l.to} to={l.to}
                            className={`tb-link text-sm tracking-wide whitespace-nowrap ${loc.pathname === l.to ? "text-[var(--tb-gold)]" : ""}`}
                            data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}>
                            {l.label}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-2 sm:gap-3 md:gap-4 flex-shrink-0">
                    <Link to="/carrito" className="relative tb-link" data-testid="nav-cart">
                        <ShoppingBag className="w-5 h-5" />
                        {count > 0 && (
                            <span className="absolute -top-2 -right-2 bg-[var(--tb-gold)] text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">{count}</span>
                        )}
                    </Link>
                    {user ? (
                        <div className="hidden xl:flex items-center gap-3 min-w-0">
                            <Link to={user.role === "admin" ? "/admin" : user.role === "vendedor" ? "/vendedor" : "/perfil"}
                                className="flex items-center gap-2 tb-link min-w-0 max-w-[160px] 2xl:max-w-[240px]"
                                title={user.name}
                                data-testid="nav-profile">
                                <User className="w-4 h-4 flex-shrink-0" />
                                <span className="text-sm flex items-center gap-1 min-w-0">
                                    <span className="truncate">{getPublicName(user)}</span>
                                    {user.verified && <BadgeCheck className="w-4 h-4 text-[#2196F3] fill-[#2196F3]/20 flex-shrink-0" />}
                                </span>
                            </Link>
                            <button onClick={() => { logout(); nav("/"); }} className="tb-link flex-shrink-0" data-testid="nav-logout">
                                <LogOut className="w-4 h-4" />
                            </button>
                        </div>
                    ) : (
                        <Link to="/login" className="tb-btn-secondary hidden xl:inline-block" data-testid="nav-login">{t("Acceder", "Sign in")}</Link>
                    )}
                    <button className="xl:hidden tb-link" onClick={() => setOpen(!open)} data-testid="nav-toggle">
                        {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </div>
            {open && (
                <div className="xl:hidden border-t border-[var(--tb-border)] px-6 py-4 space-y-3 bg-[var(--tb-surface-2)]">
                    {links.map((l) => (
                        <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block tb-link" data-testid={`nav-mobile-${l.to.replace("/", "") || "home"}`}>{l.label}</Link>
                    ))}
                    {user ? (
                        <>
                            <Link to={user.role === "admin" ? "/admin" : user.role === "vendedor" ? "/vendedor" : "/perfil"} className="block tb-link" onClick={() => setOpen(false)}>
                                {user.role === "admin" ? t("Admin", "Admin") : user.role === "vendedor" ? t("Panel Vendedor", "Seller Panel") : t("Mi Perfil", "My Profile")}
                            </Link>
                            <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="block tb-link">{t("Cerrar sesión", "Sign out")}</button>
                        </>
                    ) : (
                        <Link to="/login" className="block tb-link" onClick={() => setOpen(false)}>{t("Acceder", "Sign in")}</Link>
                    )}
                </div>
            )}
        </header>
    );
}
